import React, { memo, useCallback, useState, useRef, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';
import { EyeIcon, LinkOutIcon } from './Icons';
import { getComputerTypeOptions, isComputerTypeField, getUserRiskScore, getUserComputerType } from './utils';
import PortalDropdown from './PortalDropdown';
import api from '../api';

// Memoized Row component
const Row = memo(({
  index,
  style,
  data
}) => {
  const {
    records,
    passwordVisibility,
    hoveredLink,
    hoveredEye,
    tagIdToName,
    getTagColor,
    getTagPriority,
    formatDate,
    getRiskScoreLabel,
    getRiskScoreColor,
    getTextColorForBackground,
    isDateField,
    isTagsField,
    isRiskScoreField,
    fieldLabelsDashboard,
    onTogglePasswordVisibility,
    onHoveredLink,
    onHoveredEye,
    currentUserId,
    onSolvedToggle,
    onRiskScoreChange,
    onComputerTypeChange,
    userCredentialVisibility,
    tagsOptions,
    onTagAdded,
    onTagRemoved,
  } = data;
  const rec = records[index];

  // --- Local state for row fields ---
  const [solvedChecked, setSolvedChecked] = useState(Array.isArray(rec.solved) && currentUserId != null && rec.solved.includes(Number(currentUserId)));
  const [riskScore, setRiskScore] = useState(getUserRiskScore(rec.risk_score, currentUserId));
  const [computerType, setComputerType] = useState(getUserComputerType(rec.computer_type, currentUserId));
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const tagButtonRef = useRef();
  const solvedDebounce = useRef();
  const riskScoreDebounce = useRef();
  const computerTypeDebounce = useRef();

  useEffect(() => {
    setSolvedChecked(Array.isArray(rec.solved) && currentUserId != null && rec.solved.includes(Number(currentUserId)));
  }, [rec.id, rec.solved, currentUserId]);

  useEffect(() => {
    const userRiskScore = getUserRiskScore(rec.risk_score, currentUserId);
    setRiskScore(userRiskScore);
  }, [rec.id, rec.risk_score, currentUserId]);

  useEffect(() => {
    const userComputerType = getUserComputerType(rec.computer_type, currentUserId);
    setComputerType(userComputerType);
  }, [rec.id, rec.computer_type, currentUserId]);


  // Tag management functions
  const handleAddTag = useCallback(async (tagId) => {
    try {
      await api.addTagToRecord(rec.id, tagId);
      if (onTagAdded) {
        onTagAdded(rec.id, tagId);
      }
      setShowTagDropdown(false);
    } catch (error) {
      console.error('Failed to add tag:', error);
    }
  }, [rec.id, onTagAdded]);

  const handleRemoveTag = useCallback(async (tagId) => {
    try {
      await api.removeTagFromRecord(rec.id, tagId);
      if (onTagRemoved) {
        onTagRemoved(rec.id, tagId);
      }
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  }, [rec.id, onTagRemoved]);

  const handleSolvedChange = (e) => {
    const checked = e.target.checked;
    setSolvedChecked(checked);
    if (solvedDebounce.current) clearTimeout(solvedDebounce.current);
    solvedDebounce.current = setTimeout(() => {
      if (onSolvedToggle) onSolvedToggle(rec.id, checked);
    }, 500);
  };

  // Risk score change handler
  const handleRiskScoreChange = (e) => {
    const newScore = Number(e.target.value);
    setRiskScore(newScore);
    if (riskScoreDebounce.current) clearTimeout(riskScoreDebounce.current);
    riskScoreDebounce.current = setTimeout(() => {
      if (onRiskScoreChange) onRiskScoreChange(rec.id, newScore, currentUserId);
    }, 500);
  };

  // Computer type change handler
  const handleComputerTypeChange = (e) => {
    const newType = e.target.value;
    setComputerType(newType);
    if (computerTypeDebounce.current) clearTimeout(computerTypeDebounce.current);
    computerTypeDebounce.current = setTimeout(() => {
      if (onComputerTypeChange) onComputerTypeChange(rec.id, newType, currentUserId);
    }, 50);
  };

  return (
    <tr
      key={index}
      style={{
        ...style,
        display: 'flex',
        width: '100%',
        background: index % 2 === 0 ? '#374151' : '#1f2937',
        borderBottom: '1px solid var(--border)',
      }}
      onMouseOver={e => (e.currentTarget.style.background = '#4b5563')}
      onMouseOut={e => (e.currentTarget.style.background = index % 2 === 0 ? '#374151' : '#1f2937')}
    >
      {/* Checkbox cell for solved status */}
      <td style={{ width: 36, minWidth: 36, maxWidth: 36, padding: '0.6rem', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <input
          type="checkbox"
          checked={solvedChecked}
          onChange={handleSolvedChange}
          disabled={!onSolvedToggle}
          style={{ cursor: onSolvedToggle ? 'pointer' : 'not-allowed' }}
          title={solvedChecked ? 'Solved' : 'Unsolved'}
        />
      </td>
      {Object.keys(fieldLabelsDashboard).map((field) => {
        const cellContent = (() => {
          if (isDateField(field)) {
            const dateValue = formatDate(rec[field]);
            return dateValue === '-' ? (
              <span style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>-</span>
            ) : dateValue;
          }
                     if (field === 'password') {
             if (!rec[field]) return '-';
             return (
               <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                 <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                   {userCredentialVisibility && passwordVisibility[rec.id]
                     ? rec[field]
                     : `${'*'.repeat(Math.max(0, rec[field].length - 3))}${rec[field].slice(-3)}`}
                 </span>
                 {userCredentialVisibility && (
                   <button
                     type="button"
                     onClick={() => onTogglePasswordVisibility(rec.id)}
                     onMouseEnter={() => onHoveredEye(index)}
                     onMouseLeave={() => onHoveredEye(null)}
                     style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                   >
                     <EyeIcon open={passwordVisibility[rec.id]} hovered={hoveredEye === index} />
                   </button>
                 )}
               </span>
             );
           }
                       if (field === 'user') {
              if (!rec[field]) return '-';
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {userCredentialVisibility
                      ? rec[field]
                      : (() => {
                          const atIndex = rec[field].indexOf('@');
                          if (atIndex === -1 || atIndex <= 5) {
                            // If no @ or @ is at position 5 or less, mask all but last 3
                            return `${'*'.repeat(Math.max(0, rec[field].length - 3))}${rec[field].slice(-3)}`;
                          } else {
                            // Mask last 5 characters before @
                            const beforeAt = rec[field].substring(0, atIndex);
                            const afterAt = rec[field].substring(atIndex);
                            const visiblePart = beforeAt.substring(0, Math.max(0, beforeAt.length - 5));
                            const maskedPart = '*'.repeat(Math.min(5, beforeAt.length));
                            return `${visiblePart}${maskedPart}${afterAt}`;
                          }
                        })()}
                  </span>
                </span>
              );
            }
          if (isTagsField(field) && Array.isArray(rec[field])) {
            const visibleTags = rec[field].filter(id => tagIdToName[id]).sort((a, b) => {
              const priorityA = getTagPriority(a);
              const priorityB = getTagPriority(b);
              if (priorityA !== priorityB) {
                return priorityB - priorityA; // Higher priority first
              }
              // If same priority, sort alphabetically by name
              return tagIdToName[a].localeCompare(tagIdToName[b]);
            });
            const availableTags = tagsOptions ? tagsOptions.filter(tag => !rec[field].includes(String(tag.id))) : [];
            
            return (
              <div style={{ position: 'relative' }}>
                <div style={{ 
                  display: 'flex', 
                  gap: 4, 
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  maxWidth: '100%',
                  scrollbarWidth: 'none', /* Firefox */
                  msOverflowStyle: 'none', /* IE and Edge */
                  WebkitScrollbar: { display: 'none' } /* Chrome, Safari, Opera */
                }} className="tags-scroll-container">
                  {availableTags.length > 0 && (
                    <button
                      ref={tagButtonRef}
                      style={{
                        background: '#374151',
                        color: '#9ca3af',
                        border: '1px dashed #6b7280',
                        borderRadius: 8,
                        padding: '0.1rem 0.6rem',
                        fontSize: '0.85em',
                        fontWeight: 500,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        minWidth: '20px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={() => setShowTagDropdown(!showTagDropdown)}
                      title="Add tag"
                    >
                      +
                    </button>
                  )}
                  {visibleTags.map(tagId => (
                    <span
                      key={tagId}
                      style={{ 
                        background: getTagColor(tagId), 
                        color: '#fff', 
                        borderRadius: 8, 
                        padding: '0.1rem 0.6rem', 
                        fontSize: '0.85em', 
                        fontWeight: 500,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        position: 'relative'
                      }}
                      onClick={() => handleRemoveTag(tagId)}
                      title="Click to remove tag"
                    >
                      {tagIdToName[tagId]} ×
                    </span>
                  ))}
                </div>
                
                <PortalDropdown
                  isOpen={showTagDropdown && availableTags.length > 0}
                  onClose={() => setShowTagDropdown(false)}
                  triggerRef={tagButtonRef}
                >
                  {availableTags.map((tag) => (
                    <div
                      key={tag.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.3rem 0.7rem',
                        cursor: 'pointer',
                        fontSize: 12,
                        color: '#e5e7eb'
                      }}
                      onClick={() => handleAddTag(tag.id)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      <span style={{
                        width: 12,
                        height: 12,
                        borderRadius: 2,
                        backgroundColor: tag.color,
                        marginRight: 8,
                        flexShrink: 0
                      }}></span>
                      {tag.name}
                    </div>
                  ))}
                </PortalDropdown>
              </div>
            );
          }
          if (isRiskScoreField(field)) {
            const color = getRiskScoreColor(riskScore);
            if (onRiskScoreChange && riskScore && [1,2,3,4,5].includes(Number(riskScore))) {
              return (
                <select
                  value={riskScore || ''}
                  onChange={handleRiskScoreChange}
                  style={{
                    background: color,
                    color: getTextColorForBackground(color),
                    borderRadius: 8,
                    padding: '0.1rem 0.6rem',
                    fontSize: '0.85em',
                    fontWeight: 500,
                    minWidth: 60,
                    textAlign: 'center',
                    border: 'none',
                    outline: 'none',
                  }}
                >
                  <option value="1">Low</option>
                  <option value="2">Medium</option>
                  <option value="3">High</option>
                  <option value="4">Very High</option>
                  <option value="5">Critical</option>
                </select>
              );
            }
            // If not editable, just show dash or label
            if (!riskScore || riskScore === '-' || ![1,2,3,4,5].includes(Number(riskScore))) {
              return <span style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>-</span>;
            }
            return (
              <span
                style={{
                  background: color,
                  color: getTextColorForBackground(color),
                  borderRadius: 8,
                  padding: '0.1rem 0.6rem',
                  fontSize: '0.85em',
                  fontWeight: 500,
                  minWidth: 60,
                  textAlign: 'center',
                }}
              >
                {getRiskScoreLabel(riskScore)}
              </span>
            );
          }
          if (isComputerTypeField(field)) {
            const hasType = computerType;
            if (onComputerTypeChange && hasType) {
              const options = getComputerTypeOptions();
              return (
                <select
                  value={computerType || 'UNKNOWN'}
                  onChange={handleComputerTypeChange}
                  style={{
                    borderRadius: 8,
                    padding: '0.1rem 0.6rem',
                    fontSize: '0.85em',
                    fontWeight: 500,
                    minWidth: 90,
                    textAlign: 'center',
                    border: 'none',
                    outline: 'none',
                    background: '#23293a',
                    color: '#fff',
                  }}
                >
                  {options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              );
            } else {
              return <span style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>-</span>;
            }
          }
          if (field === 'domain_type') {
            if (rec[field] === 1) return 'Primary';
            if (rec[field] === 2) return 'Third-party';
            return <span style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>-</span>;
          }
          if (field === 'domain_or_url') {
            const value = rec.url || rec.domain;
            if (!value) return <span style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>-</span>;
            const isUrl = Boolean(rec.url);
            const displayValue = isUrl ? value : `@${String(value).replace(/^@+/, '')}`;
            return (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {displayValue}
                </span>
              </span>
            );
          }
          const value = rec[field] || '-';
          return value === '-' ? (
            <span style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>-</span>
          ) : value;
        })();

        return (
          <td
            key={field}
            style={{ 
              flex: isTagsField(field) ? 0.6 : 1, 
              padding: '0.6rem', 
              overflow: isTagsField(field) ? 'hidden' : 'hidden', 
              textOverflow: isTagsField(field) ? 'clip' : 'ellipsis', 
              whiteSpace: isTagsField(field) ? 'normal' : 'nowrap',
              maxWidth: isTagsField(field) ? '200px' : 'none'
            }}
            title={(() => {
              if (isDateField(field)) return rec[field] ? formatDate(rec[field]) : '-';
                             if (field === 'password') {
                 if (!rec[field]) return '-';
                 return passwordVisibility[rec.id]
                   ? rec[field]
                   : `${'*'.repeat(Math.max(0, rec[field].length - 3))}${rec[field].slice(-3)}`;
               }
                               if (field === 'user') {
                  if (!rec[field]) return '-';
                  return userCredentialVisibility
                    ? rec[field]
                    : (() => {
                        const atIndex = rec[field].indexOf('@');
                        if (atIndex === -1 || atIndex <= 5) {
                          // If no @ or @ is at position 5 or less, mask all but last 3
                          return `${'*'.repeat(Math.max(0, rec[field].length - 3))}${rec[field].slice(-3)}`;
                        } else {
                          // Mask last 5 characters before @
                          const beforeAt = rec[field].substring(0, atIndex);
                          const afterAt = rec[field].substring(atIndex);
                          const visiblePart = beforeAt.substring(0, Math.max(0, beforeAt.length - 5));
                          const maskedPart = '*'.repeat(Math.min(5, beforeAt.length));
                          return `${visiblePart}${maskedPart}${afterAt}`;
                        }
                      })();
                }
              if (isTagsField(field) && Array.isArray(rec[field])) {
                const visibleTags = rec[field].filter(id => tagIdToName[id]).sort((a, b) => {
                  const priorityA = getTagPriority(a);
                  const priorityB = getTagPriority(b);
                  if (priorityA !== priorityB) {
                    return priorityB - priorityA; // Higher priority first
                  }
                  // If same priority, sort alphabetically by name
                  return tagIdToName[a].localeCompare(tagIdToName[b]);
                });
                return visibleTags.map(tagId => tagIdToName[tagId] || tagId).join(', ');
              }
              if (isRiskScoreField(field)) {
                return `Risk Score: ${riskScore || 0} (${getRiskScoreLabel(riskScore)})`;
              }
              if (isComputerTypeField(field)) {
                const options = getComputerTypeOptions();
                return options.find(opt => opt.value === computerType)?.label || 'Unknown';
              }
              if (field === 'domain_type') {
                if (rec[field] === 1) return 'Primary';
                if (rec[field] === 2) return 'Third-party';
                return '-';
              }
              if (field === 'domain_or_url') {
                if (rec.url) return rec.url;
                if (rec.domain) return `@${String(rec.domain).replace(/^@+/, '')}`;
                return '-';
              }
              return rec[field] || '-';
            })()}
          >
            {cellContent}
          </td>
        );
      })}
      <td style={{ width: 36, padding: '0.6rem', textAlign: 'center' }}>
        {rec.systemid && (
          <button
            type="button"
            onClick={() => window.open(`https://intelx.io/?did=${encodeURIComponent(rec.systemid)}`, '_blank')}
            onMouseEnter={() => onHoveredLink(index)}
            onMouseLeave={() => onHoveredLink(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <LinkOutIcon hovered={hoveredLink === index} />
          </button>
        )}
      </td>
    </tr>
  );
});

const VirtualizedTableBody = ({
  records,
  rowHeight = 50,
  maxHeight = 500,
  passwordVisibility,
  hoveredLink,
  hoveredEye,
  tagIdToName,
  getTagColor,
  getTagPriority,
  formatDate,
  getRiskScoreLabel,
  getRiskScoreColor,
  getTextColorForBackground,
  isDateField,
  isTagsField,
  isRiskScoreField,
  fieldLabelsDashboard,
  onTogglePasswordVisibility,
  onHoveredLink,
  onHoveredEye,
  currentUserId,
  onSolvedToggle,
  onRiskScoreChange,
  onComputerTypeChange,
  userCredentialVisibility,
  tagsOptions,
  onTagAdded,
  onTagRemoved,
}) => {
  const height = Math.min(maxHeight, records.length * rowHeight);
  // Prepare data object to pass to each row
  const rowData = {
    records,
    passwordVisibility,
    hoveredLink,
    hoveredEye,
    tagIdToName,
    getTagColor,
    getTagPriority,
    formatDate,
    getRiskScoreLabel,
    getRiskScoreColor,
    getTextColorForBackground,
    isDateField,
    isTagsField,
    isRiskScoreField,
    fieldLabelsDashboard,
    onTogglePasswordVisibility,
    onHoveredLink,
    onHoveredEye,
    currentUserId,
    onSolvedToggle,
    onRiskScoreChange,
    onComputerTypeChange,
    userCredentialVisibility,
    tagsOptions,
    onTagAdded,
    onTagRemoved,
  };

  return (
    <List
      height={height}
      itemCount={records.length}
      itemSize={rowHeight}
      width="100%"
      itemData={rowData}
      style={{ overflowX: 'hidden' }}
    >
      {Row}
    </List>
  );
};

export default VirtualizedTableBody;
